import {
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  Grid,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { EmptyState } from 'src/commons/EmptyState';
import { defaultWrapLinesPreference, largePageSizePreference, paginationLabels } from 'src/commons/tables';
import {
  defaultEntityBoostedFields,
  defaultTermAggregations,
  entityDisplayName,
  flatten,
  getDataSources,
  getSearchTableColumnDefinitions,
  SearchAggregations,
} from 'src/components/search/constants';
import { searchData, useServerCollections } from 'src/components/search/server-hook';
import { TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { DefaultRouteProps } from 'src/commons/common';
import { PageHeader } from 'src/components/common/PageHeader';
import { useSearchParams } from 'src/components/search/useSearchParams';

export interface SearchHomeProps extends DefaultRouteProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  catalogMap: any;
  onAggregationsChange: (newAggregations: SearchAggregations) => void;
  searchLoading: boolean;
  onSetSearchLoading: (isLoading: boolean) => void;
}

export const SearchHome = (props: SearchHomeProps) => {
  const { params, setParams } = useSearchParams();
  const { query, entityType, filters } = params;

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });
  const [filteringText, setFilteringText] = useState(query);
  const [totalItems, setTotalItems] = useState([]);
  const [nextToken, setNextToken] = useState(undefined);
  const dataSources: string[] = getDataSources(!!props.activeWorkspace);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
  }, []);

  // Sync filteringText with URL query param
  useEffect(() => {
    setFilteringText(query);
  }, [query]);

  const { pageSize } = preferences;
  const serverParams = {
    pagination: { pageSize },
    queryString: query,
    entityType,
    filters,
    onAggregationsChange: props.onAggregationsChange,
    onSetSearchLoading: props.onSetSearchLoading,
    isWorkspace: !!props.activeWorkspace,
    catalogMap: props.catalogMap,
    nextToken: undefined,
    onNextTokenChange: (token: string) => setNextToken(token),
  };

  const { items } = useServerCollections(serverParams);

  const {
    items: pageItems,
    paginationProps,
    collectionProps,
  } = useCollection(totalItems, {
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
  });

  useEffect(() => {
    setTotalItems((current) => [...current, ...items]);
  }, [items]);

  // Reset items when search params change
  useEffect(() => {
    setTotalItems([]);
    setNextToken(undefined);
  }, [entityType, query, filters]);

  const onClearFilter = () => {
    setParams({ query: '', filters: {} });
  };

  const onQueryChange = (newQuery: string) => {
    setParams({ query: newQuery });
  };

  const onLoadNextResults = async () => {
    let searchResult = await searchData(
      [entityType],
      query,
      defaultEntityBoostedFields.get(entityType),
      pageSize,
      defaultTermAggregations.get(entityType),
      filters,
      nextToken,
      props.onSetSearchLoading,
    );
    let nextItems = flatten(searchResult.resultList, dataSources, entityType, props.catalogMap);
    setTotalItems((current) => [...current, ...nextItems]);
    setNextToken(searchResult.nextToken);
    if (searchResult.aggregations) props.onAggregationsChange(searchResult.aggregations);
  };

  const columnDefinitions = useMemo(() => {
    return getSearchTableColumnDefinitions(entityType, props.workspaceNameMap);
  }, [!!props.activeWorkspace, entityType]);

  const anyQueryFiltersState = !!query || (filters && Object.keys(filters).length > 0);
  const emptyTableDisplayString = anyQueryFiltersState ? 'No entities found!' : 'Search with prefix or full match';

  return (
    <SpaceBetween size='l'>
      <Table
        {...collectionProps}
        loadingText={`Loading ${entityDisplayName.get(entityType)}...`}
        loading={props.searchLoading}
        columnDefinitions={columnDefinitions}
        items={pageItems}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        empty={
          <EmptyState
            title='No matches'
            subtitle={emptyTableDisplayString}
            action={anyQueryFiltersState && <Button onClick={onClearFilter}>Clear filters</Button>}
          />
        }
        header={
          <PageHeader buttons={[]} counter={`(${totalItems.length})`} header={entityDisplayName.get(entityType)} />
        }
        filter={
          <Grid gridDefinition={[{ colspan: 5 }, { colspan: 5 }]}>
            <TextFilter
              filteringText={filteringText}
              onChange={({ detail }) => setFilteringText(detail.filteringText)}
              onDelayedChange={() => onQueryChange(filteringText)}
              filteringAriaLabel={`Filter ${entityType}`}
              filteringPlaceholder='Search with prefix or full match'
              filteringClearAriaLabel='Clear'
              countText={`${totalItems.length} ${totalItems.length === 1 ? 'match' : 'matches'}`}
            />
            <Button onClick={onLoadNextResults} disabled={!nextToken}>
              Load more results...
            </Button>
          </Grid>
        }
        pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        preferences={
          <CollectionPreferences
            title='Preferences'
            confirmLabel='Confirm'
            cancelLabel='Cancel'
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={largePageSizePreference}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
      />
    </SpaceBetween>
  );
};
